import { useState, useEffect, useRef } from 'react';

/**
 * useVideoLuminosity
 * 
 * Custom hook that samples video frames to detect background luminosity
 * and returns whether the logo should be white or black for optimal contrast.
 * 
 * @param videoRef - Reference to the video element
 * @returns 'white' | 'black' - Logo color for optimal contrast
 */
export function useVideoLuminosity(videoRef: React.RefObject<HTMLVideoElement>) {
    const [logoColor, setLogoColor] = useState<'white' | 'black'>('white');
    const logoColorRef = useRef<'white' | 'black'>('white');
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const smoothedLuminosityRef = useRef<number | null>(null);
    const lastColorChangeAtRef = useRef<number>(0);
    const candidateColorRef = useRef<'white' | 'black' | null>(null);
    const candidateCountRef = useRef<number>(0);

    useEffect(() => {
        // Create canvas element for sampling
        if (!canvasRef.current) {
            canvasRef.current = document.createElement('canvas');
        }

        if (!videoRef.current) return;
        
        const video = videoRef.current;
        const canvas = canvasRef.current;
        // `willReadFrequently` helps a lot on Safari/iOS when calling getImageData repeatedly
        const ctx = canvas.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings);
        if (!ctx) return;

        let corsReadFailed = false;

        const interval = setInterval(() => {
            try {
                if (corsReadFailed) return;
                if (video.readyState < 2) return; // Not enough data yet
                
                // Downsample for performance (critical for iOS crash prevention)
                // Instead of full 1080p, we only need a tiny thumb to check brightness
                const sampleWidth = 50; 
                const sampleHeight = 50; // Aspect ratio doesn't matter for luminosity average
                
                canvas.width = sampleWidth;
                canvas.height = sampleHeight;
                
                // Draw downsampled frame
                ctx.drawImage(video, 0, 0, sampleWidth, sampleHeight);
                
                // Sample area where logo appears (center-top, approximately 95vw)
                // Since we downsampled, we use relative percentages of the small canvas
                const logoAreaWidth = Math.floor(sampleWidth * 0.95);
                const logoAreaHeight = Math.floor(sampleHeight * 0.25);
                const logoAreaX = Math.floor((sampleWidth - logoAreaWidth) / 2);
                const logoAreaY = 0;
                
                // This will throw SecurityError if canvas is tainted by cross-origin video
                const imageData = ctx.getImageData(logoAreaX, logoAreaY, logoAreaWidth, logoAreaHeight);
                
                // Calculate average luminosity using standard formula
                let totalLuminosity = 0;
                for (let i = 0; i < imageData.data.length; i += 4) {
                    const r = imageData.data[i];
                    const g = imageData.data[i + 1];
                    const b = imageData.data[i + 2];
                    // Luminosity formula: 0.299R + 0.587G + 0.114B
                    totalLuminosity += (0.299 * r + 0.587 * g + 0.114 * b);
                }
                const avgLuminosity = totalLuminosity / (imageData.data.length / 4);

                // Smooth + hysteresis to avoid rapid toggling around the threshold
                const baseThreshold = 128;
                const hysteresisBand = 10; // needs to go clearly light/dark to change
                const alpha = 0.2; // exponential moving average factor

                const previousSmoothed = smoothedLuminosityRef.current;
                const smoothed = previousSmoothed === null ? avgLuminosity : (alpha * avgLuminosity + (1 - alpha) * previousSmoothed);
                smoothedLuminosityRef.current = smoothed;

                const current = logoColorRef.current;
                let desired = current;

                if (current === 'white' && smoothed >= baseThreshold + hysteresisBand) {
                    desired = 'black';
                } else if (current === 'black' && smoothed <= baseThreshold - hysteresisBand) {
                    desired = 'white';
                }

                const now = Date.now();
                const cooldownMs = 1000; // after switching, wait at least 1s before re-evaluating

                // During cooldown we don't even accumulate candidates. This prevents
                // the "flip back" happening immediately when cooldown ends.
                if (now - lastColorChangeAtRef.current < cooldownMs) {
                    candidateColorRef.current = null;
                    candidateCountRef.current = 0;
                    return;
                }

                // Require a few consecutive samples before switching.
                // This avoids quick flips on high-contrast frames.
                const requiredConsecutiveSamples = 4; // ~600ms at 150ms interval

                if (desired !== current) {
                    if (candidateColorRef.current !== desired) {
                        candidateColorRef.current = desired;
                        candidateCountRef.current = 1;
                    } else {
                        candidateCountRef.current += 1;
                    }

                    if (candidateCountRef.current >= requiredConsecutiveSamples) {
                        logoColorRef.current = desired;
                        lastColorChangeAtRef.current = now;
                        candidateColorRef.current = null;
                        candidateCountRef.current = 0;
                        setLogoColor(desired);
                    }
                } else {
                    candidateColorRef.current = null;
                    candidateCountRef.current = 0;
                }
            } catch (error) {
                // Canvas can be tainted by cross-origin video (very common on iOS Safari).
                // iOS Safari often throws "The operation is insecure" instead of "SecurityError".
                const message = error instanceof Error ? error.message : String(error);
                const isCorsError =
                    message.includes('SecurityError') ||
                    message.includes('The operation is insecure') ||
                    message.includes('insecure') ||
                    message.includes('tainted');

                if (isCorsError) {
                    corsReadFailed = true;
                    // Fallback: assume dark background
                    logoColorRef.current = 'white';
                    setLogoColor('white');
                }
            }
        }, 150); // Sample every 150ms

        return () => clearInterval(interval);
    }, [videoRef]);

    return logoColor;
}
